// Order -> GA4 `purchase` payload mapping (Phase 1, task G2).
//
// The confirmation page (/success?orderId=<id>) is the one place every completed order — paid and
// free — lands. This maps that order into the params sendGaPurchase (ga.js) expects. The order id
// is the transaction_id: the stable, one-per-order key GA4 AND Google Ads both use to de-duplicate.
//
// Conversion value = net sales BEFORE sales tax, divided by 7, rounded to cents. The pre-tax figure
// comes from the server (OrderViewModel.NetSalesBeforeTax) — TransactionAmount would include Texas
// sales tax. NetSalesBeforeTax is null for unpaid orders; Number(null)||0 => 0 (the paid-only gate
// on the success page already prevents firing for $0 orders).

const round2 = n => Math.round(n * 100) / 100;

/**
 * @param {Object} order      The loaded order (OrderViewModel shape).
 * @param {string} orderId    The id from the /success?orderId= query param (authoritative).
 * @returns {{ transactionId: string, value: number, currency: string, items: Array }}
 */
export const buildPurchasePayload = (order, orderId) => {
  const conversionValue = round2((Number(order.NetSalesBeforeTax) || 0) / 7);
  return {
    transactionId: orderId,
    value: conversionValue,
    currency: "USD",
    items: [
      {
        // Product actually paid for — server-sourced from the order's succeeded PaymentAttempt.
        // Fall back to the order id / a generic name if absent (free order / older server).
        item_id: order.ProductSku || String(orderId),
        item_name: order.ProductDescription || "Form 2290 E-File",
        price: conversionValue,
        quantity: 1
      }
    ]
  };
};

// Refresh-safe dedupe ledger (G3). /success can be refreshed and revisited, so a `purchase` must
// fire at most once per order id. We use localStorage (not sessionStorage) so a later revisit — even
// in a new session — is still covered. The list is bounded and every access is guarded, because a
// ledger write failing (private mode / quota) must never break the page: the platform-level dedupe
// on transaction_id (GA4 + Google Ads) is the safety net.
const LEDGER_KEY = "ga_purchase_sent";
const MAX_LEDGER = 50;

const readLedger = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(LEDGER_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
};

export const alreadySent = id => readLedger().includes(String(id));

export const markSent = id => {
  try {
    const next = [
      ...readLedger().filter(x => x !== String(id)),
      String(id)
    ].slice(-MAX_LEDGER);
    localStorage.setItem(LEDGER_KEY, JSON.stringify(next));
  } catch (e) {
    // private mode / quota — platform transaction_id dedupe still covers us.
  }
};
